import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Login } from "../services/loginServices";
import { useNavigate } from "react-router-dom";

const LoginPage = () => {
  const { login } = useAuth();
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");

  const [errors, setErrors] = useState({ emailOrUsername: "", password: "" });
  const [serverError, setServerError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // --- helpers ---
  const validate = () => {
    const next = { emailOrUsername: "", password: "" };
    if (!emailOrUsername.trim()) {
      next.emailOrUsername = "Please enter your email or username.";
    } else if (/\s/.test(emailOrUsername)) {
      next.emailOrUsername = "No spaces allowed.";
    }

    if (!password) {
      next.password = "Please enter your password.";
    } else if (password.length < 6) {
      next.password = "Password must be at least 6 characters.";
    }

    setErrors(next);
    return !next.emailOrUsername && !next.password;
  };

  const parseApiError = (err) => {
    // Works with axios-style and fetch-style errors
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Something went wrong. Please try again.";
    return msg;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await Login({
        email: emailOrUsername,
        password,
      });

      // store in auth and go
      login(response.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Failed to login:", err);
      setServerError(parseApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
      <div className="card shadow border-0" style={{ width: 420 }}>
        <div className="card-body p-4">
          <div className="mb-3 text-center">
            <h3 className="fw-semibold mb-1">Welcome back</h3>
            <p className="text-secondary mb-0">Sign in to your account</p>
          </div>

          {/* top-level error banner */}
          {serverError && (
            <div
              role="alert"
              aria-live="assertive"
              className="alert alert-danger d-flex align-items-start gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                className="bi bi-exclamation-triangle mt-1"
                viewBox="0 0 16 16"
                aria-hidden="true"
              >
                <path d="M7.938 2.016a1.13 1.13 0 0 1 2.124 0l6.857 12.5c.457.833-.119 1.984-1.062 1.984H2.143c-.943 0-1.52-1.151-1.062-1.984l6.857-12.5zM8 5c-.535 0-.954.462-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 5zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
              </svg>
              <span>{serverError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* email/username */}
            <div className="mb-3">
              <label htmlFor="login-identifier" className="form-label">
                Email or Username
              </label>
              <input
                id="login-identifier"
                type="text"
                className={`form-control ${
                  errors.emailOrUsername ? "is-invalid" : ""
                }`}
                placeholder="Username or Email"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                onBlur={validate}
                aria-invalid={!!errors.emailOrUsername}
                aria-describedby="login-identifier-help login-identifier-error"
                autoComplete="username"
                disabled={isSubmitting}
              />
              <div id="login-identifier-help" className="form-text">
                Use your email or username.
              </div>
              {errors.emailOrUsername && (
                <div id="login-identifier-error" className="invalid-feedback">
                  {errors.emailOrUsername}
                </div>
              )}
            </div>

            {/* password */}
            <div className="mb-3">
              <label htmlFor="login-password" className="form-label">
                Password
              </label>
              <div className="input-group">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${
                    errors.password ? "is-invalid" : ""
                  }`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={validate}
                  aria-invalid={!!errors.password}
                  aria-describedby="login-password-error"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={isSubmitting}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
                {errors.password && (
                  <div
                    id="login-password-error"
                    className="invalid-feedback d-block"
                  >
                    {errors.password}
                  </div>
                )}
              </div>
            </div>

            {/* submit */}
            <button
              type="submit"
              className="btn btn-primary w-100 py-2 d-flex justify-content-center align-items-center"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {isSubmitting ? "Signing in..." : "Sign in"}
            </button>

            {/* subtle footer */}
            <p
              className="text-center text-muted mt-3 mb-0"
              style={{ fontSize: 13 }}
            >
              By continuing you agree to our Terms and Privacy Policy.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
